import React, { useState, useEffect, useCallback } from 'react';
import { Search, RefreshCw, Loader2, ChevronLeft, ChevronRight, Mail } from 'lucide-react';
import * as api from '../api';

const STATUTS = [
  { value: '',          label: 'Tous les statuts' },
  { value: 'sent',      label: 'Envoyé' },
  { value: 'delivered', label: 'Délivré' },
  { value: 'opened',    label: 'Ouvert' },
  { value: 'failed',    label: 'Échec' },
  { value: 'skipped',   label: 'Filtré' },
  { value: 'mock',      label: 'Mock' },
];

const STATUT_STYLE = {
  sent:      { bg: '#EFF6FF', color: '#1D4ED8', label: 'Envoyé' },
  delivered: { bg: '#F0FDF4', color: '#166534', label: 'Délivré' },
  opened:    { bg: '#ECFDF5', color: '#065F46', label: 'Ouvert' },
  failed:    { bg: '#FEF2F2', color: '#991B1B', label: 'Échec' },
  erreur:    { bg: '#FEF2F2', color: '#991B1B', label: 'Erreur' },
  mock:      { bg: '#F5F3FF', color: '#6D28D9', label: 'Mock' },
  envoye:    { bg: '#EFF6FF', color: '#1D4ED8', label: 'Envoyé' },
  partiel:   { bg: '#FFFBEB', color: '#B45309', label: 'Partiel' },
  termine:   { bg: '#F0FDF4', color: '#166534', label: 'Terminé' },
  skipped:   { bg: '#FFF7ED', color: '#C2410C', label: 'Filtré' },
};

const TABS = [
  { key: 'transactionnel', label: 'Transactionnel' },
  { key: 'campagne',       label: 'Campagne' },
  { key: 'systeme',        label: 'Système' },
];

const inp = {
  border: '1px solid var(--border, #E2E8F0)', borderRadius: 6, padding: '5px 9px',
  fontSize: 13, background: '#fff', boxSizing: 'border-box',
};
const btnGhost = {
  display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px',
  background: 'transparent', color: 'var(--text-muted, #6B7280)',
  border: '1px solid var(--border, #E2E8F0)', borderRadius: 6, fontSize: 13, cursor: 'pointer',
};

function StatutBadge({ statut }) {
  const s = STATUT_STYLE[statut] || { bg: '#F3F4F6', color: '#374151', label: statut };
  return (
    <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10,
      background: s.bg, color: s.color, fontWeight: 500, whiteSpace: 'nowrap' }}>
      {s.label}
    </span>
  );
}

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('fr-FR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function skipLabel(msg) {
  if (!msg) return null;
  const clean = msg.replace(/^Skipped\s*:\s*/i, '').split(' — ')[0].trim();
  if (/cr.dit non lettré/i.test(clean)) {
    const m = clean.match(/([\d.,]+\s*\u202f?€)/);
    return m ? `Crédit non lettré (${m[1]})` : 'Crédit non lettré';
  }
  if (/solde.*cr.diteur/i.test(clean)) {
    const m = clean.match(/([\d.,]+\s*\u202f?€)/);
    return m ? `Solde créditeur (${m[1]})` : 'Solde créditeur';
  }
  if (/erreur api/i.test(clean) || /pennylane/i.test(clean)) return 'Erreur API Pennylane';
  if (/email/i.test(clean)) return 'Pas d\'email';
  if (/entreprise/i.test(clean)) return 'Pas d\'entreprise';
  return clean.length > 45 ? clean.slice(0, 42) + '…' : clean;
}

const TD = { padding: '8px 12px' };
const TDMuted = { ...TD, color: 'var(--text-muted)', fontSize: 12 };
const TDEllipsis = (maxW) => ({ ...TD, maxWidth: maxW, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' });

function RowTransactionnel({ row, i }) {
  return (
    <tr key={row.envoi_id || i} data-testid={`log-row-${row.envoi_id}`}
      style={{ borderBottom: '1px solid var(--border)', background: i % 2 === 0 ? '#fff' : '#FAFAFA' }}>
      <td style={{ ...TDMuted, whiteSpace: 'nowrap' }}>{fmtDate(row.created_at)}</td>
      <td style={TDEllipsis(160)}>
        {row.personne_nom
          ? <span style={{ fontWeight: 500 }}>{row.personne_nom}</span>
          : <span style={{ color: 'var(--text-muted)' }}>—</span>}
      </td>
      <td style={{ ...TDEllipsis(200), ...TDMuted }}>{row.recipient_email || '—'}</td>
      <td style={{ ...TDEllipsis(160), ...TDMuted }}>{row.entreprise_nom || '—'}</td>
      <td style={{ ...TDEllipsis(160), ...TDMuted }}>{row.target_entity_id || '—'}</td>
      <td style={{ ...TDEllipsis(260), fontSize: 13 }} title={row.sujet_snapshot || ''}>
        {row.sujet_snapshot || <span style={{ color: '#D1D5DB' }}>—</span>}
      </td>
      <td style={TD}><StatutBadge statut={row.statut} /></td>
    </tr>
  );
}

function RowCampagne({ row, i }) {
  const motif = row.statut === 'skipped' ? skipLabel(row.error_message) : null;
  return (
    <tr key={row.envoi_id || i} data-testid={`log-row-${row.envoi_id}`}
      style={{ borderBottom: '1px solid var(--border)', background: i % 2 === 0 ? '#fff' : '#FAFAFA' }}>
      <td style={{ ...TDMuted, whiteSpace: 'nowrap' }}>{fmtDate(row.created_at)}</td>
      <td style={TDEllipsis(160)}>
        {row.personne_nom
          ? <span style={{ fontWeight: 500 }}>{row.personne_nom}</span>
          : <span style={{ color: 'var(--text-muted)' }}>—</span>}
      </td>
      <td style={{ ...TDEllipsis(200), ...TDMuted }}>{row.recipient_email || '—'}</td>
      <td style={{ ...TDEllipsis(160), ...TDMuted }}>{row.entreprise_nom || row.periode_label || '—'}</td>
      <td style={{ ...TDEllipsis(180), fontWeight: 500 }}>{row.campaign_label || '—'}</td>
      <td style={{ ...TDEllipsis(260), fontSize: 13 }} title={row.sujet_snapshot || ''}>
        {row.sujet_snapshot || <span style={{ color: '#D1D5DB' }}>—</span>}
      </td>
      <td style={TD}><StatutBadge statut={row.statut} /></td>
      <td style={TD}>
        {motif ? (
          <span data-testid={`log-motif-${row.envoi_id}`} title={row.error_message}
            style={{ fontSize: 11, padding: '2px 8px', borderRadius: 6,
              background: '#FFF7ED', color: '#C2410C',
              border: '1px solid #FED7AA', fontWeight: 500,
              whiteSpace: 'nowrap', display: 'inline-block' }}>
            {motif}
          </span>
        ) : <span style={{ color: '#D1D5DB', fontSize: 12 }}>—</span>}
      </td>
    </tr>
  );
}

function RowSysteme({ row, i }) {
  const objet = row.sujet_snapshot || row.template_nom || '—';
  return (
    <tr key={row.envoi_id || i} data-testid={`log-row-${row.envoi_id}`}
      style={{ borderBottom: '1px solid var(--border)', background: i % 2 === 0 ? '#fff' : '#FAFAFA' }}>
      <td style={{ ...TDMuted, whiteSpace: 'nowrap' }}>{fmtDate(row.created_at)}</td>
      <td style={TDEllipsis(160)}>
        {row.personne_nom
          ? <span style={{ fontWeight: 500 }}>{row.personne_nom}</span>
          : <span style={{ color: 'var(--text-muted)' }}>—</span>}
      </td>
      <td style={{ ...TDEllipsis(200), ...TDMuted }}>{row.recipient_email || '—'}</td>
      <td style={{ ...TDEllipsis(180), fontWeight: 500 }}>{row.template_nom || '—'}</td>
      <td style={{ ...TDEllipsis(280), fontSize: 13 }} title={objet}>
        {objet !== '—' ? objet : <span style={{ color: '#D1D5DB' }}>—</span>}
      </td>
      <td style={TD}><StatutBadge statut={row.statut} /></td>
    </tr>
  );
}

const HEADERS = {
  transactionnel: ['Date', 'Contact', 'Email', 'Entreprise', 'Document', 'Objet', 'Statut'],
  campagne:       ['Date', 'Contact', 'Email', 'Entreprise', 'Campagne', 'Objet', 'Statut', 'Motif de filtrage'],
  systeme:        ['Date', 'Contact', 'Email', 'Modèle', 'Objet', 'Statut'],
};

export default function EmailLogs() {
  const [activeTab, setActiveTab] = useState('transactionnel');
  const [items,     setItems]     = useState([]);
  const [total,     setTotal]     = useState(0);
  const [loading,   setLoading]   = useState(true);
  const [page,      setPage]      = useState(1);
  const [q,         setQ]         = useState('');
  const [statut,    setStatut]    = useState('');
  const [campaigns, setCampaigns] = useState([]);
  const [campId,    setCampId]    = useState('');

  const LIMIT = 50;

  const load = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const params = { page: p, limit: LIMIT, email_type: activeTab };
      if (q.trim())  params.q          = q.trim();
      if (statut)    params.statut      = statut;
      if (campId && activeTab === 'campagne') params.campaign_id = campId;
      const r = await api.getEmailLogs(params);
      setItems(r.data?.items || []);
      setTotal(r.data?.total || 0);
      setPage(p);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [q, statut, campId, activeTab]);

  useEffect(() => {
    api.getMailingTypes().then(r => setCampaigns(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => { load(1); }, [load]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setQ('');
    setStatut('');
    setCampId('');
    setPage(1);
  };

  const totalPages = Math.max(1, Math.ceil(total / LIMIT));
  const headers    = HEADERS[activeTab];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>Logs d'envoi</h2>
          <p style={{ margin: 0, fontSize: 13, color: 'var(--text-muted)' }}>
            {total.toLocaleString('fr-FR')} email{total !== 1 ? 's' : ''} enregistré{total !== 1 ? 's' : ''}
          </p>
        </div>
        <button onClick={() => load(page)} style={btnGhost} data-testid="logs-refresh">
          <RefreshCw size={14} />
        </button>
      </div>

      {/* Onglets */}
      <div style={{ display: 'flex', borderBottom: '2px solid var(--border, #E2E8F0)', gap: 0 }}>
        {TABS.map(t => (
          <button key={t.key} data-testid={`logs-tab-${t.key}`}
            onClick={() => handleTabChange(t.key)}
            style={{
              padding: '8px 20px', fontSize: 13, fontWeight: activeTab === t.key ? 600 : 400,
              color: activeTab === t.key ? '#1D4ED8' : 'var(--text-muted, #6B7280)',
              background: 'none', border: 'none', borderBottom: activeTab === t.key ? '2px solid #1D4ED8' : '2px solid transparent',
              marginBottom: -2, cursor: 'pointer', transition: 'color 0.15s',
            }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Filtres */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: '1 1 220px' }}>
          <Search size={13} style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
          <input data-testid="logs-search" value={q} onChange={e => setQ(e.target.value)}
            placeholder={activeTab === 'campagne' ? 'Contact, email, société, objet…' : 'Contact, email, objet…'}
            style={{ ...inp, width: '100%', paddingLeft: 28 }} />
        </div>
        <select value={statut} onChange={e => setStatut(e.target.value)}
          style={{ ...inp, flex: '0 0 160px' }} data-testid="logs-filter-statut">
          {STATUTS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        {activeTab === 'campagne' && (
          <select value={campId} onChange={e => setCampId(e.target.value)}
            style={{ ...inp, flex: '0 0 200px' }} data-testid="logs-filter-campaign">
            <option value="">Toutes les campagnes</option>
            {campaigns.map(c => (
              <option key={c.campaign_id} value={c.campaign_id}>{c.label}</option>
            ))}
          </select>
        )}
      </div>

      {/* Tableau */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>
          <Loader2 size={24} className="animate-spin" />
        </div>
      ) : items.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)', fontSize: 14,
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
          <Mail size={32} style={{ opacity: 0.3 }} />
          Aucun log trouvé pour ces critères.
        </div>
      ) : (
        <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#F8FAFC', borderBottom: '1px solid var(--border)' }}>
                {headers.map(h => (
                  <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600,
                    fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((row, i) =>
                activeTab === 'transactionnel' ? <RowTransactionnel key={row.envoi_id || i} row={row} i={i} /> :
                activeTab === 'campagne'       ? <RowCampagne       key={row.envoi_id || i} row={row} i={i} /> :
                                                 <RowSysteme        key={row.envoi_id || i} row={row} i={i} />
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8 }}>
          <button onClick={() => load(page - 1)} disabled={page <= 1} style={{ ...btnGhost, padding: '5px 10px' }}>
            <ChevronLeft size={14} />
          </button>
          <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
            Page {page} / {totalPages}
          </span>
          <button onClick={() => load(page + 1)} disabled={page >= totalPages} style={{ ...btnGhost, padding: '5px 10px' }}>
            <ChevronRight size={14} />
          </button>
        </div>
      )}
    </div>
  );
}
